(function (global) {
  'use strict';

  var ROOT_SELECTOR = '[data-qy-upcoming-observance]';
  var BLOCK_SELECTOR = '[data-qy-observance-block]';
  var EMPTY_SELECTOR = '[data-qy-observance-empty], [data-qy-observance-editor-empty]';
  var SOURCE_SELECTOR = '[data-qy-observance-source], source[data-src], source[data-srcset]';
  var IMAGE_SELECTOR = '[data-qy-observance-image], img[data-src], img[data-srcset]';
  var DEFAULT_TIME_ZONE = 'Asia/Shanghai';
  var DEFAULT_REFRESH_INTERVAL = 60000;
  var DAY_IN_MS = 86400000;
  var controllers = typeof WeakMap === 'function' ? new WeakMap() : null;

  if (global.QiyuanUpcomingObservance && global.QiyuanUpcomingObservance.version) {
    if (global.document && global.QiyuanUpcomingObservance.init) {
      global.QiyuanUpcomingObservance.init(global.document);
    }
    return;
  }

  function toArray(value) {
    return Array.prototype.slice.call(value || []);
  }

  function matches(element, selector) {
    return Boolean(element && element.matches && element.matches(selector));
  }

  function queryAll(scope, selector) {
    return scope && scope.querySelectorAll ? toArray(scope.querySelectorAll(selector)) : [];
  }

  function readData(element, attributeName) {
    if (!element || !element.getAttribute) return null;
    var value = element.getAttribute(attributeName);
    return value === null ? null : String(value);
  }

  function hasData(element, attributeName) {
    return Boolean(element && element.hasAttribute && element.hasAttribute(attributeName));
  }

  function parseISODate(value) {
    if (typeof value !== 'string') return null;

    var match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) return null;

    var year = Number(match[1]);
    var month = Number(match[2]);
    var date = Number(match[3]);
    if (year < 1000 || year > 9999 || month < 1 || month > 12 || date < 1 || date > 31) return null;

    var timestamp = Date.UTC(year, month - 1, date);
    var parsed = new Date(timestamp);
    if (
      parsed.getUTCFullYear() !== year ||
      parsed.getUTCMonth() !== month - 1 ||
      parsed.getUTCDate() !== date
    ) {
      return null;
    }

    return {
      key: value,
      day: timestamp / DAY_IN_MS,
      year: year,
      month: month,
      date: date,
    };
  }

  function dateKeyFromUTC(date) {
    return [
      String(date.getUTCFullYear()).padStart(4, '0'),
      String(date.getUTCMonth() + 1).padStart(2, '0'),
      String(date.getUTCDate()).padStart(2, '0'),
    ].join('-');
  }

  function shanghaiDateKey(date) {
    return dateKeyFromUTC(new Date(date.getTime() + 8 * 60 * 60 * 1000));
  }

  function getTodayKey(now, timeZone) {
    var date = now instanceof Date ? new Date(now.getTime()) : new Date(now === undefined ? Date.now() : now);
    if (Number.isNaN(date.getTime())) return null;

    var zone = timeZone || DEFAULT_TIME_ZONE;
    try {
      var formatter = new Intl.DateTimeFormat('en-US-u-ca-gregory-nu-latn', {
        timeZone: zone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      var values = {};
      formatter.formatToParts(date).forEach(function (part) {
        if (part.type === 'year' || part.type === 'month' || part.type === 'day') {
          values[part.type] = part.value;
        }
      });
      var key = [values.year, values.month, values.day].join('-');
      if (parseISODate(key)) return key;
    } catch (error) {
      // Asia/Shanghai has no daylight-saving transition, so the fixed offset is a safe fallback.
    }

    return shanghaiDateKey(date);
  }

  function isManualShow(value) {
    return value === true || value === 'true' || value === '1';
  }

  function isBlank(value) {
    return value === null || value === undefined || value === '';
  }

  function orderValue(value, fallback) {
    if (isBlank(value)) return fallback;
    var number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function eligibleCandidate(record, today, index) {
    if (!record || !isManualShow(record.manualShow)) return null;

    var observanceDate = parseISODate(record.observanceDate);
    if (!observanceDate) return null;

    var displayStart = null;
    if (!isBlank(record.displayStart)) {
      displayStart = parseISODate(record.displayStart);
      if (!displayStart) return null;
    }

    var displayEnd = null;
    if (!isBlank(record.displayEnd)) {
      displayEnd = parseISODate(record.displayEnd);
      if (!displayEnd) return null;
    }

    var startDay = displayStart ? displayStart.day : Number.NEGATIVE_INFINITY;
    var effectiveEndDay = Math.min(observanceDate.day, displayEnd ? displayEnd.day : observanceDate.day);

    if (startDay > observanceDate.day || startDay > effectiveEndDay) return null;
    if (today.day < startDay || today.day > effectiveEndDay) return null;

    return {
      record: record,
      dateDay: observanceDate.day,
      order: orderValue(record.blockOrder, index),
      index: index,
      effectiveEndDay: effectiveEndDay,
    };
  }

  function selectObservance(records, options) {
    var settings = typeof options === 'string' ? { todayKey: options } : options || {};
    var todayKey = settings.todayKey || getTodayKey(settings.now, settings.timeZone || DEFAULT_TIME_ZONE);
    var today = parseISODate(todayKey);
    if (!today) return null;

    var candidates = [];
    toArray(records).forEach(function (record, index) {
      var candidate = eligibleCandidate(record, today, index);
      if (candidate) candidates.push(candidate);
    });

    candidates.sort(function (left, right) {
      return left.dateDay - right.dateDay || left.order - right.order || left.index - right.index;
    });

    return candidates.length ? candidates[0].record : null;
  }

  function setHidden(element, hidden) {
    if (!element) return;
    element.hidden = Boolean(hidden);
  }

  function copyAttribute(element, deferredName, liveName) {
    if (!element || !element.getAttribute || !element.setAttribute) return false;
    var value = element.getAttribute(deferredName);
    if (value === null || value === '') return false;
    element.setAttribute(liveName, value);
    return true;
  }

  function hydrateBlock(block) {
    if (!block) return 0;

    var hydrated = 0;
    queryAll(block, SOURCE_SELECTOR).forEach(function (source) {
      var copied =
        copyAttribute(source, 'data-srcset', 'srcset') ||
        copyAttribute(source, 'data-src', 'srcset');
      if (copied) {
        copyAttribute(source, 'data-sizes', 'sizes');
        source.setAttribute('data-qy-hydrated', 'true');
        hydrated += 1;
      }
    });

    queryAll(block, IMAGE_SELECTOR).forEach(function (image) {
      var copiedSrcset = copyAttribute(image, 'data-srcset', 'srcset');
      var copiedSrc = copyAttribute(image, 'data-src', 'src');
      if (copiedSrcset || copiedSrc) {
        copyAttribute(image, 'data-sizes', 'sizes');
        image.setAttribute('data-qy-hydrated', 'true');
        hydrated += 1;
      }
    });

    return hydrated;
  }

  function collectRecords(root) {
    return queryAll(root, BLOCK_SELECTOR).map(function (block, index) {
      return {
        element: block,
        manualShow: readData(block, 'data-manual-show'),
        observanceDate: readData(block, 'data-observance-date'),
        displayStart: readData(block, 'data-display-start'),
        displayEnd: readData(block, 'data-display-end'),
        blockOrder: readData(block, 'data-block-order'),
        index: index,
      };
    });
  }

  function render(root, selectedBlock, showEditorEmpty) {
    var blocks = queryAll(root, BLOCK_SELECTOR);
    var empty = queryAll(root, EMPTY_SELECTOR)[0] || null;

    blocks.forEach(function (block) {
      setHidden(block, block !== selectedBlock);
    });
    setHidden(empty, true);

    if (selectedBlock) {
      hydrateBlock(selectedBlock);
      setHidden(root, false);
      if (root.setAttribute) {
        var blockId = readData(selectedBlock, 'data-block-id');
        if (blockId) root.setAttribute('data-active-block-id', blockId);
      }
      return selectedBlock;
    }

    if (root.removeAttribute) root.removeAttribute('data-active-block-id');
    if (showEditorEmpty && empty) {
      setHidden(empty, false);
      setHidden(root, false);
    } else {
      setHidden(root, true);
    }
    return null;
  }

  function flagValue(element, attributeName) {
    if (!hasData(element, attributeName)) return null;
    var value = readData(element, attributeName);
    return value === '' || value === 'true' || value === '1';
  }

  function detectDesignMode(root, explicitValue) {
    if (explicitValue !== undefined) return Boolean(explicitValue);
    var rootValue = flagValue(root, 'data-design-mode');
    if (rootValue !== null) return rootValue;
    return Boolean(global.Shopify && global.Shopify.designMode);
  }

  function closestBlock(target) {
    if (matches(target, BLOCK_SELECTOR)) return target;
    return target && target.closest ? target.closest(BLOCK_SELECTOR) : null;
  }

  function ObservanceController(root, options) {
    this.root = root;
    this.options = options || {};
    this.document = this.options.document || global.document || null;
    this.window = this.options.window || global.window || global;
    this.timeZone = this.options.timeZone || readData(root, 'data-time-zone') || DEFAULT_TIME_ZONE;
    this.refreshInterval = this.options.refreshInterval || DEFAULT_REFRESH_INTERVAL;
    this.getNow = this.options.getNow || function () {
      return new Date();
    };
    this.setInterval =
      this.options.setInterval ||
      (this.window && this.window.setInterval ? this.window.setInterval.bind(this.window) : global.setInterval);
    this.clearInterval =
      this.options.clearInterval ||
      (this.window && this.window.clearInterval ? this.window.clearInterval.bind(this.window) : global.clearInterval);
    this.designMode = detectDesignMode(root, this.options.designMode);
    this.connected = false;
    this.timerId = null;
    this.editorBlock = null;
    this.sectionSelected = false;

    this.onVisibilityChange = this.handleVisibilityChange.bind(this);
    this.onPageShow = this.refresh.bind(this);
    this.onBlockSelect = this.handleBlockSelect.bind(this);
    this.onBlockDeselect = this.handleBlockDeselect.bind(this);
    this.onSectionSelect = this.handleSectionSelect.bind(this);
    this.onSectionDeselect = this.handleSectionDeselect.bind(this);
  }

  ObservanceController.prototype.contains = function (element) {
    return Boolean(element && this.root && this.root.contains && this.root.contains(element));
  };

  ObservanceController.prototype.matchesSection = function (event) {
    var detail = (event && event.detail) || {};
    var sectionId = detail.sectionId === undefined ? null : String(detail.sectionId);
    var rootSectionId = readData(this.root, 'data-section-id');

    if (sectionId && rootSectionId) return sectionId === rootSectionId;
    if (sectionId && this.root.closest) {
      var wrapper = this.root.closest('[id^="shopify-section-"]');
      if (wrapper && wrapper.id === 'shopify-section-' + sectionId) return true;
    }

    var target = event && event.target;
    return Boolean(target && (target === this.root || (target.contains && target.contains(this.root))));
  };

  ObservanceController.prototype.refresh = function () {
    if (!this.root || !this.connected) return null;

    if (this.editorBlock && this.contains(this.editorBlock)) {
      return render(this.root, this.editorBlock, false);
    }

    this.editorBlock = null;
    var selected = selectObservance(collectRecords(this.root), {
      now: this.getNow(),
      timeZone: this.timeZone,
    });

    return render(
      this.root,
      selected ? selected.element : null,
      this.designMode && this.sectionSelected
    );
  };

  ObservanceController.prototype.handleVisibilityChange = function () {
    if (!this.document || this.document.visibilityState !== 'hidden') this.refresh();
  };

  ObservanceController.prototype.handleBlockSelect = function (event) {
    if (!this.designMode) return;
    var block = closestBlock(event && event.target);
    if (!block || !this.contains(block)) return;
    this.editorBlock = block;
    render(this.root, block, false);
  };

  ObservanceController.prototype.handleBlockDeselect = function (event) {
    if (!this.designMode) return;
    var block = closestBlock(event && event.target);
    if (block && !this.contains(block)) return;
    if (block && this.editorBlock && block !== this.editorBlock) return;
    if (!block && !this.matchesSection(event)) return;
    this.editorBlock = null;
    this.refresh();
  };

  ObservanceController.prototype.handleSectionSelect = function (event) {
    if (!this.designMode || !this.matchesSection(event)) return;
    this.sectionSelected = true;
    this.refresh();
  };

  ObservanceController.prototype.handleSectionDeselect = function (event) {
    if (!this.designMode || !this.matchesSection(event)) return;
    this.sectionSelected = false;
    this.editorBlock = null;
    this.refresh();
  };

  ObservanceController.prototype.connect = function () {
    if (this.connected) return this;
    this.connected = true;

    if (this.document && this.document.addEventListener) {
      this.document.addEventListener('visibilitychange', this.onVisibilityChange);
      if (this.designMode) {
        this.document.addEventListener('shopify:block:select', this.onBlockSelect);
        this.document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
        this.document.addEventListener('shopify:section:select', this.onSectionSelect);
        this.document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
      }
    }
    if (this.window && this.window.addEventListener) {
      this.window.addEventListener('pageshow', this.onPageShow);
    }
    if (this.setInterval) {
      this.timerId = this.setInterval(this.refresh.bind(this), this.refreshInterval);
    }

    this.refresh();
    return this;
  };

  ObservanceController.prototype.disconnect = function () {
    if (!this.connected) return;
    this.connected = false;

    if (this.timerId !== null && this.clearInterval) {
      this.clearInterval(this.timerId);
      this.timerId = null;
    }
    if (this.document && this.document.removeEventListener) {
      this.document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      this.document.removeEventListener('shopify:section:select', this.onSectionSelect);
      this.document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
    }
    if (this.window && this.window.removeEventListener) {
      this.window.removeEventListener('pageshow', this.onPageShow);
    }

    this.editorBlock = null;
    this.sectionSelected = false;
  };

  function rootsWithin(scope) {
    var roots = [];
    if (matches(scope, ROOT_SELECTOR)) roots.push(scope);
    queryAll(scope, ROOT_SELECTOR).forEach(function (root) {
      if (roots.indexOf(root) === -1) roots.push(root);
    });
    return roots;
  }

  function getController(root) {
    return controllers ? controllers.get(root) : root.__qyUpcomingObservanceController;
  }

  function setController(root, controller) {
    if (controllers) controllers.set(root, controller);
    else root.__qyUpcomingObservanceController = controller;
  }

  function removeController(root) {
    if (controllers) controllers.delete(root);
    else delete root.__qyUpcomingObservanceController;
  }

  function init(scope, options) {
    var initialized = [];
    rootsWithin(scope || global.document).forEach(function (root) {
      var controller = getController(root);
      if (!controller) {
        controller = new ObservanceController(root, options);
        setController(root, controller);
      }
      controller.connect();
      initialized.push(controller);
    });
    return initialized;
  }

  function disconnect(scope) {
    rootsWithin(scope).forEach(function (root) {
      var controller = getController(root);
      if (controller) controller.disconnect();
      removeController(root);
    });
  }

  var api = {
    version: '1.0.0',
    DEFAULT_TIME_ZONE: DEFAULT_TIME_ZONE,
    parseISODate: parseISODate,
    getTodayKey: getTodayKey,
    selectObservance: selectObservance,
    collectRecords: collectRecords,
    hydrateBlock: hydrateBlock,
    render: render,
    ObservanceController: ObservanceController,
    init: init,
    disconnect: disconnect,
    getController: getController,
  };

  global.QiyuanUpcomingObservance = api;

  if (global.customElements && global.HTMLElement && !global.customElements.get('qiyuan-upcoming-observance')) {
    var QiyuanUpcomingObservanceElement = function () {
      return Reflect.construct(global.HTMLElement, [], QiyuanUpcomingObservanceElement);
    };
    QiyuanUpcomingObservanceElement.prototype = Object.create(global.HTMLElement.prototype);
    QiyuanUpcomingObservanceElement.prototype.constructor = QiyuanUpcomingObservanceElement;
    Object.setPrototypeOf(QiyuanUpcomingObservanceElement, global.HTMLElement);
    QiyuanUpcomingObservanceElement.prototype.connectedCallback = function () {
      init(this);
    };
    QiyuanUpcomingObservanceElement.prototype.disconnectedCallback = function () {
      disconnect(this);
    };
    global.customElements.define('qiyuan-upcoming-observance', QiyuanUpcomingObservanceElement);
  }

  if (global.document && global.document.addEventListener) {
    global.document.addEventListener('shopify:section:load', function (event) {
      init(event.target);
    });
    global.document.addEventListener('shopify:section:unload', function (event) {
      disconnect(event.target);
    });

    if (global.document.readyState === 'loading') {
      global.document.addEventListener('DOMContentLoaded', function () {
        init(global.document);
      });
    } else {
      init(global.document);
    }
  }
})(typeof globalThis !== 'undefined' ? globalThis : this);
